import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as DownArrowIcon } from 'assets/icons/down-arrow-icon.svg';
import { RoutePath } from 'router/routes/routePath';
import { Structure } from 'api';
import { StoreyDropdownMenu } from './menu';

const SWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;
`;

const SButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px;
    border: 1px solid ${({ theme }) => theme.colors.light_gray};
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.white};
    cursor: pointer;
    font-size: ${({ theme }) => theme.font.size.small};
    transition: ${({ theme }) => theme.transition.all};
    white-space: nowrap;

    &:hover {
        background-color: ${({ theme }) => theme.colors.highlight};
        border-color: ${({ theme }) => theme.colors.primary};
    }

    svg {
        width: 16px;
        height: 16px;
        fill: ${({ theme }) => theme.colors.revolver};
        transition: ${({ theme }) => theme.transition.all};
    }
`;

interface Props {
    storeys: Structure[];
    currentStorey: Structure;
    className?: string;
}

export const StoreyDropdown = ({ storeys, currentStorey, className }: Props) => {
    const [showMenu, setShowMenu] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            const target = event.target as Node;
            const menuElement = document.querySelector('.storey-dropdown-menu');

            if (menuElement && (menuElement === target || menuElement.contains(target))) {
                return;
            }

            if (buttonRef.current && (buttonRef.current === target || buttonRef.current.contains(target))) {
                return;
            }

            setShowMenu(false);
        };

        if (showMenu) {
            setTimeout(() => {
                document.addEventListener('mousedown', handleClickOutside);
            }, 100);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMenu]);

    const openMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setShowMenu(true);
    };

    const handleStoreySelect = (storey: Structure) => {
        setShowMenu(false);

        if (storey.reference) {
            const path = generatePath(RoutePath.StructuresPreview, {
                structureId: storey.reference.id,
            });
            const queryParams = new URLSearchParams({ isReference: 'true' });
            window.open(`${path}?${queryParams.toString()}`, '_blank');
        } else {
            const path = generatePath(RoutePath.StructuresPreview, {
                structureId: storey.id,
            });
            navigate(path);
        }
    };

    return (
        <SWrapper className={className}>
            <SButton onClick={openMenu} ref={buttonRef}>
                {currentStorey.name}
                <DownArrowIcon />
            </SButton>
            {showMenu && (
                <StoreyDropdownMenu
                    storeys={storeys}
                    currentStorey={currentStorey}
                    onClose={() => setShowMenu(false)}
                    onSelect={handleStoreySelect}
                    buttonRef={buttonRef}
                />
            )}
        </SWrapper>
    );
};

